import React, { useEffect, useState } from "react";
import Cookies from "js-cookie";
import { useParams, useNavigate } from "react-router-dom";
import { CircularProgress, Button, IconButton, Dialog, DialogContent, DialogActions } from "@mui/material";
import { getDynamicLogo } from '../getDynamicLogo';
import usePrescriptionStore from "../DataRepository/Prescription";
import { ChevronLeft } from "@mui/icons-material";
import { kioskModel } from "../Axios/AxiosConfig";
import DeleteIcon from '@mui/icons-material/Delete';
import EventIcon from '@mui/icons-material/Event';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import ReplayIcon from '@mui/icons-material/Replay';

/* ─── helpers ─── */
const formatDate = (raw) => {
  if (!raw) return 'N/A';
  try {
    const d = new Date(raw);
    if (isNaN(d.getTime())) return raw;
    return new Intl.DateTimeFormat('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata',
    }).format(d);
  } catch {
    return raw;
  }
};

const formatShortDate = (raw) => {
  if (!raw) return '';
  try {
    const d = new Date(raw);
    if (isNaN(d.getTime())) return raw;
    return new Intl.DateTimeFormat('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: 'Asia/Kolkata',
    }).format(d);
  } catch {
    return raw;
  }
};

function Prescription() {

  const { setPrescription, resetPrescriptionState, selectedPatientdetails, setIsFollowUpEdit } = usePrescriptionStore();

  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [shouldRefetch, setShouldRefetch] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [prescriptionToDelete, setPrescriptionToDelete] = useState(null);

  const navigate = useNavigate();

  const doctorDetailsCookie = Cookies.get("Doctor_details");
  let doctorDetails = {};
  if (doctorDetailsCookie) {
    try { doctorDetails = JSON.parse(doctorDetailsCookie); } catch (e) { }
  }

  const kioskDetailsCookie = Cookies.get("kiosk_details");
  let kioskDetails = {};
  if (kioskDetailsCookie) {
    try { kioskDetails = JSON.parse(kioskDetailsCookie); } catch (e) { }
  }

  const getPrescription = async () => {
    try {
      setLoading(true);
      const res = await kioskModel.post("/api/v1/getPrescriptionforDoctor", {
        PatientID: selectedPatientdetails?.UID || selectedPatientdetails?.uid || selectedPatientdetails?.PID,
      });
      if (res.status === 200) {
        setPrescriptions(res.data);
        
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getPrescription();
  }, []);

  const handleNavigate = (data) => {
    setIsFollowUpEdit(false);
    setPrescription(data);
    navigate(data.draftStatus ? "/prescription" : "/ViewPrescription");
  };

  const handleFollowUpEdit = (data) => {
    setIsFollowUpEdit(true);
    setPrescription(data);
    navigate("/prescription");
  };

  const deletePrescriptionById = async (id, PatientID) => {
    try {
      setLoading(true);
      const res = await kioskModel.delete("/api/v1/DeletePrescription", {
        params: { id, PatientID }
      });
      if (res.status === 200) {
        setOpenDialog(false);
        setPrescriptionToDelete(null);
        setShouldRefetch(prev => !prev);
        getPrescription();
      }
    } catch (err) {
      console.error("Error deleting prescription:", err);
    } finally {
      setLoading(false);
    }
  };

  // Sort by most recent date first
  const sortedPrescriptions = [...prescriptions].sort((a, b) => {
    const da = new Date(a.Date);
    const db = new Date(b.Date);
    if (isNaN(da.getTime()) && isNaN(db.getTime())) return 0;
    if (isNaN(da.getTime())) return 1;
    if (isNaN(db.getTime())) return -1;
    return db - da;
  });

  return (
    <>
      {/* ─── Nav ─── */}
      <nav style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 12px',
        margin: '8px 8px 0',
        borderRadius: '10px',
        border: '1px solid #E0DDD6',
        backgroundColor: '#FFFFFF',
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate(-1)}>
            <ChevronLeft />
          </IconButton>
          <p style={{
            margin: 0,
            fontSize: '18px',
            fontWeight: 500,
            fontFamily: "'Poppins', sans-serif",
            color: 'var(--themePrimaryColor, #3B362A)',
          }}>Prescriptions</p>
        </div>
        {sortedPrescriptions.length > 0 && (
          <Button
            startIcon={<AddIcon />}
            sx={{
              textTransform: 'none',
              backgroundColor: 'var(--themePrimaryColor, #DA6E0B)',
              color: 'white',
              borderRadius: '8px',
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 500,
              padding: '6px 16px',
              fontSize: '13px',
              '&:hover': { backgroundColor: '#b55a08' },
            }}
            onClick={() => {
              setPrescription(null);
              navigate("/prescription");
            }}
          >
            New Prescription
          </Button>
        )}
      </nav>

      {/* ─── Content ─── */}
      <div style={{ padding: '1rem' }}>
        {sortedPrescriptions.length > 0 ? (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '1rem',
          }}>
            {sortedPrescriptions.map((prescription, index) => {
              const isDraft = prescription.draftStatus;
              const doctorName = prescription.Appointment_details?.doctor_name || kioskDetails?.name || 'Doctor';
              const followUp = prescription.followUpDetails || prescription.FollowUp_details;
              const hasFollowUp = followUp && (followUp.date || followUp.followUp === 'Yes');

              return (
                <div
                  key={prescription?.id || index}
                  onClick={() => handleNavigate(prescription)}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    border: '1px solid #E0DDD6',
                    borderRadius: '12px',
                    backgroundColor: '#FFFFFF',
                    padding: '1rem',
                    cursor: 'pointer',
                    fontFamily: "'Poppins', sans-serif",
                    transition: 'box-shadow 0.2s ease, transform 0.15s ease',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                    position: 'relative',
                  }}
                  onMouseEnter={(ev) => {
                    ev.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                    ev.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(ev) => {
                    ev.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)';
                    ev.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {/* Top row: Rx icon + status badge + actions */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.75rem',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '8px',
                        backgroundColor: isDraft ? '#FFF3E6' : '#EDF7ED',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <img src="/icons/Rx.png" alt="Rx"
                          style={{ width: '22px' }} />
                      </div>
                      <span style={{
                        fontSize: '11px',
                        fontWeight: 600,
                        color: isDraft ? 'var(--themePrimaryColor, #DA6E0B)' : '#2e7d32',
                        backgroundColor: isDraft ? '#FFF3E6' : '#EDF7ED',
                        borderRadius: '4px',
                        padding: '2px 8px',
                      }}>
                        {isDraft ? 'Draft' : 'Completed'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                      {isDraft ? (
                        <>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNavigate(prescription);
                            }}
                            sx={{ color: 'var(--themePrimaryColor, #DA6E0B)', '&:hover': { backgroundColor: '#FFF3E6' } }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPrescriptionToDelete(prescription);
                              setOpenDialog(true);
                            }}
                            sx={{ color: '#bbb', '&:hover': { color: '#c62828', backgroundColor: '#fef2f2' } }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <Button
                            size="small"
                            startIcon={<ReplayIcon sx={{ fontSize: '16px !important' }} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleFollowUpEdit(prescription);
                            }}
                            sx={{
                              textTransform: 'none',
                              fontSize: '11px',
                              color: 'var(--themePrimaryColor, #DA6E0B)',
                              borderRadius: '6px',
                              padding: '2px 8px',
                              minWidth: 'auto',
                              fontFamily: "'Poppins', sans-serif",
                              fontWeight: 600,
                              '&:hover': { backgroundColor: '#FFF3E6' },
                            }}
                          >
                            Follow-up
                          </Button>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNavigate(prescription);
                            }}
                            sx={{ color: 'var(--themePrimaryColor, #312110)', '&:hover': { backgroundColor: '#F5F0E6' } }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPrescriptionToDelete(prescription);
                              setOpenDialog(true);
                            }}
                            sx={{ color: '#bbb', '&:hover': { color: '#c62828', backgroundColor: '#fef2f2' } }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Doctor name */}
                  <p style={{
                    margin: '0 0 4px 0',
                    fontWeight: 600,
                    fontSize: '14px',
                    color: 'var(--themePrimaryColor, #3B362A)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {doctorName}
                  </p>

                  {/* Date */}
                  <p style={{
                    margin: '0 0 4px 0',
                    fontSize: '12px',
                    color: '#999',
                    fontWeight: 400,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}>
                    <EventIcon sx={{ fontSize: '14px', color: '#bbb' }} />
                    {formatDate(prescription.Date)}
                  </p>

                  {/* Follow-up info (for completed prescriptions) */}
                  {!isDraft && hasFollowUp && (
                    <div style={{
                      marginTop: '8px',
                      padding: '8px 10px',
                      backgroundColor: '#FFF8F2',
                      borderRadius: '8px',
                      border: '1px solid #FFE0C0',
                    }}>
                      <p style={{
                        margin: 0,
                        fontSize: '11px',
                        fontWeight: 600,
                        color: 'var(--themePrimaryColor, #DA6E0B)',
                      }}>
                        Follow-up {followUp.date ? `on ${formatShortDate(followUp.date)}` : 'Scheduled'}
                      </p>
                      {followUp.remarks && (
                        <p style={{
                          margin: '2px 0 0',
                          fontSize: '11px',
                          color: '#8B7355',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {followUp.remarks}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          !loading && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '3rem 1rem',
              textAlign: 'center',
            }}>
              <img
                src="https://aiwellassets.s3.ap-south-1.amazonaws.com/no+prescription.png"
                alt="No prescription"
                style={{ width: '200px', marginBottom: '1.5rem', opacity: 0.8 }}
              />
              <p style={{
                margin: '0 0 0.5rem',
                fontWeight: 600,
                fontSize: '18px',
                color: 'var(--themePrimaryColor, #3B362A)',
                fontFamily: "'Poppins', sans-serif",
              }}>No Prescriptions Yet</p>
              <p style={{
                margin: '0 0 1.5rem',
                fontSize: '13px',
                color: '#999',
                fontFamily: "'Poppins', sans-serif",
                maxWidth: '300px',
              }}>Create the first prescription for this patient to get started.</p>
              <Button
                startIcon={<AddIcon />}
                sx={{
                  textTransform: 'none',
                  backgroundColor: 'var(--themePrimaryColor, #DA6E0B)',
                  color: 'white',
                  borderRadius: '10px',
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 500,
                  padding: '10px 28px',
                  fontSize: '14px',
                  '&:hover': { backgroundColor: '#b55a08' },
                }}
                onClick={() => {
                  setPrescription(null);
                  navigate("/prescription");
                }}
              >
                New Prescription
              </Button>
            </div>
          )
        )}
      </div>

      {/* ─── Loading overlay ─── */}
      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "var(--themeSurfaceColor, #FDFDFD)",
            zIndex: 9999,
          }}
        >
          <div style={{ position: "relative", width: "10rem", height: "10rem" }}>
            <CircularProgress
              style={{ width: "100%", height: "100%", color: "var(--themePrimaryColor, #DA6E0B)" }}
              thickness={1}
            />
            <div
              style={{
                position: "absolute",
                top: 0, left: 0, right: 0, bottom: 0,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <img src={getDynamicLogo()} style={{ objectFit: 'contain', width: "80%", height: "80%" }} alt="Loading" />
            </div>
          </div>
        </div>
      )}

      {/* ─── Delete Draft Dialog ─── */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            padding: '0.5rem',
            maxWidth: '360px',
            fontFamily: "'Poppins', sans-serif",
          }
        }}
      >
        <DialogContent sx={{ textAlign: 'center', pt: 3 }}>
          <div style={{
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            backgroundColor: '#FEF2F2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1rem',
          }}>
            <DeleteIcon style={{ color: '#c62828', fontSize: '28px' }} />
          </div>
          <p style={{
            margin: '0 0 0.25rem 0',
            fontWeight: 600,
            fontSize: '18px',
            color: 'var(--themePrimaryColor, #3B362A)',
            fontFamily: "'Poppins', sans-serif",
          }}>{prescriptionToDelete?.draftStatus ? "Delete Draft?" : "Delete Prescription?"}</p>
          <p style={{
            margin: 0,
            fontSize: '13px',
            color: '#999',
            fontFamily: "'Poppins', sans-serif",
          }}>This action cannot be undone.</p>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: '0.75rem', pb: 3, px: 3 }}>
          <Button
            onClick={() => setOpenDialog(false)}
            variant="outlined"
            fullWidth
            sx={{
              textTransform: 'none',
              borderRadius: '10px',
              borderColor: '#E0DDD6',
              color: 'var(--themePrimaryColor, #3B362A)',
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 500,
              padding: '10px',
              '&:hover': { borderColor: '#bbb', backgroundColor: '#FAFAF8' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => deletePrescriptionById(prescriptionToDelete?.id, prescriptionToDelete?.PatientID)}
            variant="contained"
            fullWidth
            sx={{
              textTransform: 'none',
              borderRadius: '10px',
              backgroundColor: '#c62828',
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 500,
              padding: '10px',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#b71c1c', boxShadow: 'none' },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Prescription;
